/**
 * Endpoints del módulo RIC (`/api/ric_*` en EDSuite API).
 */

import { apiClient } from '@/lib/http/api-client';

import type {
  EdicionTomaPayload,
  IngresosPreview,
  RicAux,
  RicConsolidado,
  RicFiltros,
  RicRegistro,
  RicReporte,
  TomaPayload,
  TomaRic,
} from './ric.types';

export const ricKeys = {
  all: ['ric'] as const,
  aux: ['ric', 'aux'] as const,
  reporte: (filtros: RicFiltros) => ['ric', 'reporte', filtros] as const,
  registro: (id: number) => ['ric', 'registro', id] as const,
  ultima: (tank: number) => ['ric', 'ultima', tank] as const,
  visual: (anio: number, mes: number) => ['ric', 'visual', anio, mes] as const,
  osinerming: (anio: number, mes: number) => ['ric', 'osinerming', anio, mes] as const,
} as const;

function limpiar(filtros: Record<string, unknown>): Record<string, string> {
  const query: Record<string, string> = {};

  for (const [clave, valor] of Object.entries(filtros)) {
    if (valor === '' || valor === undefined || valor === null) continue;
    query[clave] = String(valor);
  }

  return query;
}

export const ricApi = {
  auxiliares: (signal?: AbortSignal): Promise<RicAux> =>
    apiClient.get<RicAux>('/api/ric_aux', { signal }),

  reporte: (filtros: RicFiltros, signal?: AbortSignal): Promise<RicReporte> =>
    apiClient.get<RicReporte>('/api/ric_report', { query: limpiar({ ...filtros }), signal }),

  registro: (id: number, signal?: AbortSignal): Promise<RicRegistro> =>
    apiClient.get<RicRegistro>(`/api/ric_record/${id}`, { signal }),

  /** Última toma activa del tanque (o una fecha sugerida). */
  ultimaToma: (tank: number, signal?: AbortSignal): Promise<{ ric_date: string; ric_physical?: number }> =>
    apiClient.get<{ ric_date: string; ric_physical?: number }>('/api/ric_last_measurement', {
      query: { tank },
      signal,
    }),

  /** Comprueba si ya hay una toma activa con esa fecha y hora. */
  duplicado: (tank: number, datetime: string): Promise<{ duplicate: boolean }> =>
    apiClient.get<{ duplicate: boolean }>('/api/ric_duplicate', { query: { tank, datetime } }),

  /** Ingresos estimados del día operativo anterior (ayuda de captura). */
  ingresosPreview: (tank: number, datetime: string): Promise<IngresosPreview> =>
    apiClient.get<IngresosPreview>('/api/ric_incomes_preview', {
      query: { tank, datetime },
    }),

  crearToma: (payload: TomaPayload): Promise<{ message: string; ric_id: number }> =>
    apiClient.post<{ message: string; ric_id: number }>('/api/ric_measurement', { body: payload }),

  editarToma: (id: number, payload: EdicionTomaPayload): Promise<{ message: string }> =>
    apiClient.patch<{ message: string }>(`/api/ric_measurement/${id}`, { body: payload }),

  anularToma: (id: number, reason: string): Promise<{ message: string }> =>
    apiClient.post<{ message: string }>(`/api/ric_measurement/${id}/cancel`, { body: { reason } }),

  visual: (anio: number, mes: number, signal?: AbortSignal): Promise<RicConsolidado> =>
    apiClient.get<RicConsolidado>('/api/ric_visual', {
      query: { year: anio, month: mes },
      signal,
    }),

  osinerming: (anio: number, mes: number, signal?: AbortSignal): Promise<RicConsolidado> =>
    apiClient.get<RicConsolidado>('/api/ric_osinerming', {
      query: { year: anio, month: mes },
      signal,
    }),
} as const;

/** Reexportado para tipar las mutaciones. */
export type { TomaRic };
